import { useCallback, useContext } from 'react';

import { logger } from '@packages/utilities';
import { TrackingContext } from '../../components/TrackingProvider';
import type { GenericTrackingEvent } from '../../types';

const dispatchEvent = <T extends GenericTrackingEvent>(
  event: T,
  dataLayerName = 'dataLayer',
): void => {
  if (typeof window === 'undefined') return;

  const dataLayer = (window as { [key: string]: any })[dataLayerName];

  if (dataLayer) {
    dataLayer.push(event);
  } else {
    logger.warn(
      `[useTracking] window.${dataLayerName} is not defined, event could not be pushed.`,
      event,
    );
  }
};

export const useTracking = (): (<T extends GenericTrackingEvent>(event: T) => void) => {
  const context = useContext(TrackingContext);
  const dataLayerName = context?.dataLayerName;

  if (context === null || typeof dataLayerName === 'undefined') {
    logger.error(
      '[useTracking]: hook was used outside of a TrackingProvider component. Events might not be tracked correctly.',
    );
  } else if (typeof window !== 'undefined' && !(window as { [key: string]: any })[dataLayerName]) {
    (window as { [key: string]: any })[dataLayerName] = [];
  }

  return useCallback(
    <T extends GenericTrackingEvent>(event: T) => {
      if (!dataLayerName) return;

      dispatchEvent(event, dataLayerName);
    },
    [dataLayerName],
  );
};
